import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { achievements, awards } from "../../../content";
import type { AchievementEntry, AchievementImage, Award } from "../../../types/content";
import { achievementImage } from "../../../utils/achievementImages";
import { awardImages } from "../../../utils/awardImages";
import type { AwardImage } from "../../../utils/awardImages";
import { conferrals, QUIET_TINT } from "./Achievements";

/**
 * ============================================================================
 * Recognition — the Home teaser (D-069).
 * ============================================================================
 *
 * One card per entry, and every word of it comes out of `achievements.json`.
 * Nothing here is a literal, so a corrected outlet or a corrected year reaches
 * this strip without anyone remembering it exists — the same rule the martial
 * arts teaser follows.
 *
 * IT SHOWS THE ATTRIBUTION, NOT JUST THE HEADLINE. A Home strip is where an
 * over-claim would be cheapest to make and most expensive to notice: a
 * visitor who reads only this card should come away with the same
 * understanding as one who reads the page. So the card carries the subject
 * line the page carries, rendered from the same `attribution` field, rather
 * than a shorter form that quietly drops whose work it was.
 *
 * IT CARRIES THE AWARDS TOO AS OF D-101, on the owner's instruction. `awards.json`
 * had been rendering on `/achievements` alone since D-081 — which is where it
 * was first rendered anywhere at all, after sitting exported-but-unread in the
 * layer for months (the D-065 shape). Home is the page a recruiter reads first
 * and it was the one page that did not carry them.
 */
/**
 * ----------------------------------------------------------------------------
 * WHICH AWARDS REACH HOME, AND WHY IT IS DERIVED RATHER THAN PICKED (D-101)
 * ----------------------------------------------------------------------------
 * `awards.json` holds four. Two were CONFERRED BY AN ORGANISATION — Mentor of
 * the Year and the Pragati Trophy, both carrying an `issuer` — and two were WON
 * AT AN EVENT, where `issuer` is null because, as the page's own note puts it,
 * "a championship placement is conferred by the event, which `context` already
 * names".
 *
 * That distinction is the selection, and it is the right one for THIS page
 * rather than a convenience. The two event awards are the martial-arts record,
 * and the martial-arts record is already on Home in its own strip — one of them
 * is literally the same id (`karate-federation-cup-2018`) as a card the
 * MartialArts teaser renders four sections above this one. Publishing both
 * would put one placing on one page twice, in two framings, six hundred pixels
 * apart. D-067 is this repository's record of what that reads like.
 *
 * IT IS A FILTER, NOT A LIST OF IDS. An editorial `FEATURED` array is the site's
 * usual mechanism and it is the wrong one here: a fifth employer award would
 * silently not appear on Home, which is the D-089 failure — a literal that goes
 * stale the day the layer grows. `issuer !== null` cannot go stale, because it
 * asks the layer the question rather than remembering its answer.
 */
const homeAwards: Award[] = awards.filter((award: Award) => award.issuer !== null);

/**
 * One award, and the whole card is a link to the entry on `/achievements`
 * (D-100's pattern, applied to the surface D-101 adds).
 */
const AwardCard = ({ award, index }: { award: Award; index: number }): JSX.Element => {
	const reduceMotion = useReducedMotion();
	/* Derived, never typed: `times` comes from the layer's date fields, which is
	   the whole reason `alsoConferredOn` is a list and not a sentence (D-081).
	   Nothing is said where an award was conferred once — a card reading
	   "conferred once" is noise. */
	const times = 1 + (award.alsoConferredOn?.length ?? 0);
	/* THE GLIMPSE (D-105). Up to two artifacts, because the widest of these
	   cards holds two at 112px and a third would shrink all of them. Mentor of
	   the Year has exactly two and the Pragati Trophy one, so nothing is
	   dropped today — and if a third ever arrives the count is what the sweep
	   asserts, rather than a silent truncation (D-095's "no silent caps"). */
	const glimpse: readonly AwardImage[] = awardImages(award.id).slice(0, 2);

	return (
		<motion.li
			className="card-shell group relative m-0"
			initial={reduceMotion ? false : { opacity: 0, y: 24 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.5, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }}
		>
			<div className="card-face flex h-full flex-col p-6">
				<div className="flex flex-wrap items-center gap-3">
					<span className={`pill-tint pill-tint-wrap ${QUIET_TINT}`}>{award.context}</span>
					{times > 1 && (
						<span className={`pill-tint ${QUIET_TINT}`}>
							Conferred {times === 2 ? "twice" : `${times} times`}
						</span>
					)}
				</div>

				<h3 className="mt-4 text-lg font-bold leading-snug text-[var(--text-strong)]">
					<Link
						to={`/achievements#${award.id}`}
						className="rounded-[4px] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
					>
						{award.title}
					</Link>
				</h3>

				<p className="mt-2 text-sm text-[var(--text-muted)]">
					{/* `issuer` is non-null for every award in `homeAwards` by
					    construction, and the guard stays anyway: the filter is one
					    edit away from someone else's, and "null ·" is worse than
					    nothing. */}
					{award.issuer !== null && (
						<>
							<span className="font-medium text-[var(--text-primary)]">{award.issuer}</span>
							<span aria-hidden="true"> · </span>
						</>
					)}
					<span className="tabular-nums">{conferrals(award)}</span>
				</p>

				<p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[var(--text-secondary)]">
					{award.description}
				</p>

				{/* ------------------------------------------------------------------
				    A GLIMPSE, NOT A CONTROL, AND THE DISTINCTION IS THE WHOLE DESIGN.
				    This card is ONE link — a stretched `::after` on the heading covers
				    it (D-099) — and putting a lightbox trigger inside would nest an
				    interactive control in a clickable card, giving the reader two
				    targets and a screen reader two names for one thing.

				    So the thumbnails are `aria-hidden` with `alt=""`, exactly as the
				    workshop glimpse is (D-095): they are decoration BESIDE the facts
				    rather than decoration standing IN FOR them (D-100), the card's own
				    heading, issuer, dates and citation say everything, and the reader
				    who wants the certificate follows the link to `/achievements`,
				    where it opens. The alt text is not lost — it is on the enlarged
				    image there, which is the only place it describes something a
				    reader can actually study.
				    ------------------------------------------------------------------ */}
				{glimpse.length > 0 && (
					<div aria-hidden="true" className="mt-4 flex gap-2.5">
						{glimpse.map((image: AwardImage) => (
							<span
								key={image.src}
								className="block aspect-[4/3] w-24 shrink-0 overflow-hidden rounded-[10px] border border-[var(--border)] bg-[var(--surface)] p-1 sm:w-28"
							>
								{/* `object-contain`, and `h-full w-full` rather than the
								    max-* pair, for the two reasons the page card states —
								    the trophy is portrait and cover cuts its name plate
								    off, and a percentage bound against an indefinite
								    parent is not a bound (D-083). Both surfaces frame
								    these identically, which the gate checks rather than
								    trusts (D-102). */}
								<img
									src={image.src}
									alt=""
									loading="lazy"
									decoding="async"
									className="h-full w-full object-contain"
								/>
							</span>
						))}
					</div>
				)}

				{/* aria-hidden — the heading link above already names the destination. */}
				<p
					aria-hidden="true"
					className="mt-auto pt-4 text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 group-hover:text-[var(--accent-link-hover)]"
				>
					Read the citation &rarr;
				</p>
			</div>
		</motion.li>
	);
};

export default function AchievementsTeaser(): JSX.Element | null {
	/* No entries means no section, rather than a heading over nothing —
	   Constitution II.4. It cannot happen today and it is one line. */
	if (!achievements.length) return null;

	return (
		<section className="section-shell" id="achievements" aria-labelledby="achievements-teaser-heading">
			<div className="section-rail">
				<p className="section-eyebrow">External Recognition</p>

				<h2 id="achievements-teaser-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Recognition
					</span>
				</h2>

				{/* THE STANDFIRST HAD TO CHANGE WITH THE CONTENT, and this is D-066's
				    rule at its most mechanical: it read "Press and broadcast coverage
				    of the work", which was exact until this section started carrying
				    awards and would then have described half of what a reader could
				    see. A standfirst ships in the `<meta name="description">` and is
				    the one line on a page that nothing else checks. No count in it,
				    and no claim about WHO confers an award — "employer" would be true
				    of both today and is not a property the layer records. */}
				<p className="section-standfirst">
					Awards conferred on the work, and the press and broadcast coverage of it — attributed to whoever
					earned it.
				</p>

				{/* TWO COLUMNS, AND THE AWARDS ARE A NESTED STACK RATHER THAN
				    SIBLINGS IN THE SAME TRACK. The broadcast card carries a 16:9
				    picture and the award cards do not, so one grid over all three
				    with `auto-rows-fr` would stretch every award card to the
				    broadcast card's height and leave two columns of empty space —
				    equal heights are right for cards of the same KIND and wrong
				    across kinds. Nesting also means the right-hand column absorbs a
				    third award without any of this changing.

				    `auto-rows-fr` lives on the nested grid, where the cards ARE the
				    same kind, so the awards match each other. */}
				<div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
					{achievements.map((entry: AchievementEntry) => {
						const first: AchievementImage | undefined = entry.images[0];
						const src = first ? achievementImage(first.id) : undefined;
						return (
							/* THE WHOLE CARD IS ONE LINK (D-100, owner instruction), and
							   the destination carries a second job: landing on
							   `/achievements#<id>` also starts the recording, so the
							   reader who clicks a broadcast card gets the broadcast
							   rather than a page about it.

							   `overflow-hidden` on the face is why `relative` sits on
							   the SHELL: the stretched `::after` is positioned against
							   the nearest positioned ancestor, and hanging it inside a
							   clipped box would work here but would be one refactor
							   away from a hit area that stops at the picture. */
							<div key={entry.id} className="card-shell group relative">
								<article className="card-face flex h-full flex-col overflow-hidden">
									{src && first && (
										<img
											src={src}
											alt={first.alt}
											width={1600}
											height={900}
											loading="lazy"
											decoding="async"
											className="block h-auto w-full transition-transform duration-300 group-hover:scale-[1.03]"
										/>
									)}
									<div className="flex flex-1 flex-col p-6">
										<div className="flex flex-wrap items-center gap-3">
											<span className="pill-tint pill-tint-upper border-[var(--cat-indigo-border)] text-[var(--cat-indigo-text)] bg-[var(--cat-indigo-fill)]">
												{entry.outlet}
											</span>
											<span className="text-sm font-semibold tabular-nums text-[var(--text-muted)]">
												{entry.year}
											</span>
										</div>
										<h3 className="mt-4 text-lg font-bold text-[var(--text-strong)]">
											<Link
												to={`/achievements#${entry.id}`}
												className="rounded-[4px] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
											>
												{entry.title}
											</Link>
										</h3>
										<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
											{entry.summary}
										</p>
										<p className="mt-auto pt-4 text-sm text-[var(--text-faint)]">
											{entry.attribution === "team"
												? `Attributed to ${entry.subject}, and to the team who delivered it.`
												: `Attributed to ${entry.subject}.`}
										</p>

										{/* aria-hidden — the heading link above already names
										    the destination, and announcing this would give a
										    screen-reader user two links to one page. It says
										    "watch" rather than "read more" because that is
										    what the link actually does. */}
										<p
											aria-hidden="true"
											className="mt-4 text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 group-hover:text-[var(--accent-link-hover)]"
										>
											Watch the segment &rarr;
										</p>
									</div>
								</article>
							</div>
						);
					})}

					{/* Article II.4 as a property of the component: no awards means no
					    column, rather than an empty box beside the broadcast card. */}
					{homeAwards.length > 0 && (
						<ul className="grid list-none auto-rows-fr grid-cols-1 gap-5 p-0">
							{homeAwards.map((award: Award, index: number) => (
								<AwardCard key={award.id} award={award} index={index} />
							))}
						</ul>
					)}
				</div>

				<div className="mt-10">
					<Link
						to="/achievements"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						See the full record →
					</Link>
				</div>
			</div>
		</section>
	);
}
